import {
	RiArrowDropDownLine,
	RiArrowLeftSLine,
	RiArrowRightSLine,
} from '@remixicon/react';
import { useT } from '@transifex/react';
import {
	Fragment,
	useEffect,
	useId,
	useRef,
	useState,
	useSyncExternalStore,
} from 'react';
import type {
	ComponentProps,
	KeyboardEvent,
	MouseEvent,
	ReactNode,
} from 'react';

import { Button } from '@playbook/atoms/button';
import { IconButton } from '@playbook/atoms/icon-button';
import { MenuItem } from '@playbook/atoms/menu-item';
import { InputGroup } from '@playbook/compositions/input-group';
import { Menu } from '@playbook/compositions/menu';
import { cn, cv } from '@playbook/utils';
import { anchorPopover } from '@playbook/utils/anchor';

import css from './styles.module.css';

export type CalendarType = 'default' | 'range';

/** 0 is Sunday, as Figma draws it; 1 is Monday. */
export type CalendarWeekStart = 0 | 1;

const DefaultCalendarWeekStart: CalendarWeekStart = 0 as const;

/**
 *	Days are ISO dates, `YYYY-MM-DD`, not `Date` objects. A date picked in a
 *	calendar has no time and no time zone, and a `Date` carries both — the
 *	usual way a birthday moves by a day. The string is also what a native
 *	`<input type="date">` submits, and it sorts the way days do.
 */
export interface CalendarRange {
	start: string;
	end?: string;
}

type CalendarEventHandlers = {
	[K in keyof ComponentProps<'div'> as K extends `on${string}`
		? K
		: never]?: ComponentProps<'div'>[K];
};

interface CalendarDataAttributes {
	[key: `data-${string}`]: string | number | boolean | undefined;
}

interface CalendarCommonProperties
	extends CalendarEventHandlers, CalendarDataAttributes {
	weekStartsOn?: CalendarWeekStart;

	/**
	 *	Month and weekday names come from `Intl`, in this locale, since the
	 *	platform already knows every language's calendar. Left out, it is the
	 *	browser's.
	 */
	locale?: string;

	/** The day drawn as today. Left out, it is today. */
	today?: string;

	previousLabel?: string;
	nextLabel?: string;

	/** Figma's `bottom-bar` slot, under the days. */
	bottomBar?: ReactNode;
}

interface CalendarSingleProperties extends CalendarCommonProperties {
	type?: 'default';
	value?: string;
	onValueChange: (value: string) => void;
}

/**
 *	A range is picked in two presses: the first sets `start`, the second sets
 *	`end`. Pressing a day before `start`, or pressing again once both are set,
 *	starts over.
 */
interface CalendarRangeProperties extends CalendarCommonProperties {
	type: 'range';
	value?: CalendarRange;
	onValueChange: (value: CalendarRange) => void;
}

type CalendarProperties = CalendarSingleProperties | CalendarRangeProperties;

/**
 *	The viewport width the type scale steps up at, as `typography.css`. Read
 *	with `useSyncExternalStore` rather than `layout/media-query`, whose hook
 *	reports false until an effect runs and would paint two months on a phone
 *	first.
 */
const MobileQuery: string = '(max-width: 580px)';

function subscribeMobile(notify: () => void): () => void {
	const query: MediaQueryList = window.matchMedia(MobileQuery);
	query.addEventListener('change', notify);

	return () => query.removeEventListener('change', notify);
}

function toIso(date: Date): string {
	const month: string = String(date.getMonth() + 1).padStart(2, '0');
	const day: string = String(date.getDate()).padStart(2, '0');

	return `${date.getFullYear()}-${month}-${day}`;
}

/** Local midday, so no daylight-saving shift can move it to another day. */
function fromIso(iso: string): Date {
	const [year = 0, month = 1, day = 1] = iso.split('-').map(Number);

	return new Date(year, month - 1, day, 12);
}

function addDays(iso: string, days: number): string {
	const date: Date = fromIso(iso);
	date.setDate(date.getDate() + days);

	return toIso(date);
}

/** The first of the month `iso` falls in, moved by `months`. */
function monthOf(iso: string, months: number = 0): string {
	const date: Date = fromIso(iso);

	return toIso(new Date(date.getFullYear(), date.getMonth() + months, 1, 12));
}

/** The same day `months` later, or the month's last day if it has fewer. */
function shiftMonth(iso: string, months: number): string {
	const target: string = monthOf(iso, months);
	const last: string = addDays(monthOf(target, 1), -1);
	const day: string = `${target.slice(0, 8)}${iso.slice(8)}`;

	return day > last ? last : day;
}

/**
 *	Whole weeks, from the one the first falls in to the one the last falls in,
 *	so a month is four to six rows. Figma draws only the rows a month needs.
 */
function weeksOf(month: string, weekStartsOn: CalendarWeekStart): string[][] {
	const first: Date = fromIso(month);
	const lead: number = (first.getDay() - weekStartsOn + 7) % 7;
	const last: string = addDays(monthOf(month, 1), -1);

	const weeks: string[][] = [];
	let day: string = addDays(month, -lead);

	while (day <= last) {
		weeks.push(Array.from({ length: 7 }, (_, i) => addDays(day, i)));
		day = addDays(day, 7);
	}

	return weeks;
}

/**
 *	A month of days, with the arrows that move it, for picking one day or a
 *	range of them.
 *
 *	A module, not a composition, because it holds state: which month is
 *	showing. Which days are picked is the caller's: `value` in,
 *	`onValueChange` out. Which month is showing is its own, the way
 *	`modules/context-menu` owns whether its list is open. It follows `value`
 *	when a new one lands off screen, which is how a "Today" button in
 *	`bottomBar` brings the current month back.
 *
 *	The days are a `role="grid"`, with the arrow keys the ARIA grid pattern
 *	promises: one Tab stop for the whole month, arrows to move a day or a week,
 *	Page Up and Page Down to move a month. That is more than `compositions/menu`
 *	does, on purpose — a menu is a handful of Tab stops and a month is 42.
 *
 *	A `range` calendar shows two months side by side, as Figma draws it, and
 *	one below 580px of viewport, with both arrows on it.
 */
export function Calendar(properties: CalendarProperties): ReactNode {
	const {
		type = 'default',
		weekStartsOn = DefaultCalendarWeekStart,
		locale,
		today = toIso(new Date()),

		value,
		onValueChange,

		previousLabel,
		nextLabel,

		bottomBar,

		...props
	} = properties;

	const t = useT();
	const previousName: string = previousLabel ?? t('Previous month');
	const nextName: string = nextLabel ?? t('Next month');

	const instance: string = useId().replaceAll(':', '');
	const grid = useRef<HTMLDivElement>(null);

	const range: CalendarRange | undefined =
		properties.type === 'range'
			? properties.value
			: properties.value
				? { start: properties.value, end: properties.value }
				: undefined;

	/** The day `value` points at: where a range ends, or where it started. */
	const anchor: string | undefined = range?.end ?? range?.start;

	/** Opens on the month a range starts in, so both ends can be in view. */
	const [month, setMonth] = useState<string>(() =>
		monthOf(range?.start ?? today)
	);

	/**
	 *	The pane count is state, not CSS: the next arrow lives on the last pane,
	 *	so hiding the second one would lose it. Two months before the browser is asked.
	 */
	const mobile: boolean = useSyncExternalStore(
		subscribeMobile,
		() => window.matchMedia(MobileQuery).matches,
		() => false
	);
	const visible: string[] =
		type === 'range' && !mobile ? [month, monthOf(month, 1)] : [month];

	/**
	 *	A new `value` off screen brings its month into view. Adjusted during
	 *	render rather than in an effect, so the old month never paints first.
	 */
	const [seen, setSeen] = useState<string | undefined>(anchor);

	if (anchor !== seen) {
		setSeen(anchor);

		if (anchor && !visible.includes(monthOf(anchor))) {
			setMonth(
				anchor < month ? monthOf(anchor) : monthOf(anchor, 1 - visible.length)
			);
		}
	}

	/**
	 *	The day an arrow key moved to. Only the keyboard writes it, and anything
	 *	else clears it, so the effect below follows the keys and never takes
	 *	focus from a click.
	 */
	const [focused, setFocused] = useState<string | null>(null);

	useEffect(() => {
		if (focused) {
			grid.current
				?.querySelector<HTMLButtonElement>(
					`[data-date="${focused}"]:not(:disabled)`
				)
				?.focus();
		}
	}, [focused]);

	const inView = (day: string): boolean => visible.includes(monthOf(day));

	const tabStop: string =
		[focused, anchor, range?.start, today].find(
			(day): day is string => !!day && inView(day)
		) ?? month;

	const heading = new Intl.DateTimeFormat(locale, {
		month: 'long',
		year: 'numeric',
	});
	const weekday = new Intl.DateTimeFormat(locale, { weekday: 'short' });
	const weekdayLong = new Intl.DateTimeFormat(locale, { weekday: 'long' });
	const dayName = new Intl.DateTimeFormat(locale, { dateStyle: 'full' });

	/** Any week will do for the names; the first one showing is at hand. */
	const weekdays: string[] = weeksOf(month, weekStartsOn)[0] ?? [];

	function choose(day: string): void {
		setFocused(null);

		if (properties.type !== 'range') {
			properties.onValueChange(day);
			return;
		}

		const current: CalendarRange | undefined = properties.value;

		if (!current || current.end || day < current.start) {
			properties.onValueChange({ start: day });
		} else {
			properties.onValueChange({ start: current.start, end: day });
		}
	}

	/**
	 *	Moves the keyboard to another day, turning the page when it falls off
	 *	the months on screen. Moving forward keeps the new day in the last
	 *	month showing, so a range calendar pages one month at a time.
	 */
	function move(from: string, days: number, months: number): void {
		const next: string = months
			? shiftMonth(from, months)
			: addDays(from, days);

		if (!inView(next)) {
			setMonth(
				next < month ? monthOf(next) : monthOf(next, 1 - visible.length)
			);
		}

		setFocused(next);
	}

	function onKeyDown(event: KeyboardEvent<HTMLDivElement>): void {
		const from: string | undefined = (event.target as HTMLElement).dataset.date;

		if (!from) {
			return;
		}

		const steps: Record<string, [number, number]> = {
			ArrowLeft: [-1, 0],
			ArrowRight: [1, 0],
			ArrowUp: [-7, 0],
			ArrowDown: [7, 0],
			PageUp: [0, -1],
			PageDown: [0, 1],
		};

		const step: [number, number] | undefined = steps[event.key];

		if (step) {
			event.preventDefault();
			move(from, ...step);
		}
	}

	function turn(months: number): void {
		setFocused(null);
		setMonth(monthOf(month, months));
	}

	/**
	 *	Which pane's list of months is open, mirrored for `aria-expanded` the
	 *	way `modules/context-menu` mirrors its list. The browser opens, closes
	 *	and light-dismisses the popover itself.
	 */
	const [openMenu, setOpenMenu] = useState<number | null>(null);

	const monthName = new Intl.DateTimeFormat(locale, { month: 'long' });

	/**
	 *	Shows `chosen` in the pane its list was opened from. In a range the
	 *	second pane is always the month after the first, so choosing there moves
	 *	both.
	 *
	 *	Focus goes back to the month's name by hand. The browser does not return
	 *	it from a popover closed by script, and it would otherwise land on the
	 *	page, losing a keyboard user's place inside the calendar.
	 */
	function pick(
		event: MouseEvent<HTMLButtonElement>,
		chosen: string,
		pane: number,
		menuId: string
	): void {
		event.currentTarget.closest<HTMLElement>('[popover]')?.hidePopover();
		document
			.querySelector<HTMLButtonElement>(`[popovertarget="${menuId}"]`)
			?.focus();
		setOpenMenu(null);
		setFocused(null);
		setMonth(monthOf(chosen, -pane));
	}

	/**
	 *	`size` is left out inside the button group, which sets it for every
	 *	segment and warns about any that set their own. The range bar has no
	 *	group, so the arrows there carry it themselves.
	 */
	const arrows = (size?: 'sm'): [ReactNode, ReactNode] => [
		<IconButton
			key="previous"
			size={size}
			label={previousName}
			icon={<RiArrowLeftSLine />}
			onClick={() => turn(-1)}
		/>,
		<IconButton
			key="next"
			size={size}
			label={nextName}
			icon={<RiArrowRightSLine />}
			onClick={() => turn(1)}
		/>,
	];

	const [previous, next] = arrows('sm');

	return (
		<div
			{...props}
			data-type={type}
			className={cn(css.calendar, type === 'range' && css.range)}>
			<div ref={grid} className={css.months} onKeyDown={onKeyDown}>
				{visible.map((shown, index) => {
					const headingId: string = `${instance}-${shown}`;
					const menuId: string = `${instance}-months-${index}`;
					const anchor = cv({ anchor: `--${instance}-${index}` });
					const year: string = shown.slice(0, 4);
					const isFirst: boolean = index === 0;
					const isLast: boolean = index === visible.length - 1;

					return (
						<div
							/**
							 *	Keyed by position, not by month, so a pane and its
							 *	open list survive the month turning under them.
							 */
							key={index}
							className={css.month}>
							<div className={css.bar}>
								{/**
								 *	A ghost button with a chevron, as Figma draws it,
								 *	opening the twelve months of the year on screen in
								 *	`compositions/menu`. Years move with the arrows. The
								 *	span names the grid below, announces the month when
								 *	it turns, and carries the list's anchor. The list
								 *	sits beside it, not in it, so it is never part of
								 *	what is announced.
								 */}
								<span
									id={headingId}
									aria-live="polite"
									style={anchor}
									className={css.heading}>
									<Button
										variant="ghost"
										size="sm"
										label={heading.format(fromIso(shown))}
										trailingIcon={<RiArrowDropDownLine />}
										popoverTarget={menuId}
										aria-haspopup="true"
										aria-expanded={openMenu === index}
									/>
								</span>

								<div
									ref={(node) => {
										const heading: HTMLElement | null =
											node?.ownerDocument.getElementById(headingId) ?? null;

										return node && heading
											? anchorPopover(heading, node, {
													placement: 'bottomLeft',
												})
											: undefined;
									}}
									id={menuId}
									popover="auto"
									style={anchor}
									className={css.popover}
									onToggle={(event) =>
										setOpenMenu(event.newState === 'open' ? index : null)
									}>
									<Menu label={year} size="sm">
										{Array.from({ length: 12 }, (_, i) => {
											const option: string = `${year}-${String(i + 1).padStart(2, '0')}-01`;

											return (
												<MenuItem
													key={option}
													label={monthName.format(fromIso(option))}
													variant={option === shown ? 'selected' : 'default'}
													onClick={(event) =>
														pick(event, option, index, menuId)
													}
												/>
											);
										})}
									</Menu>
								</div>

								{type === 'range' ? (
									<Fragment>
										{isFirst && (
											<span className={css.previous}>{previous}</span>
										)}
										{isLast && <span className={css.next}>{next}</span>}
									</Fragment>
								) : (
									<InputGroup
										label={heading.format(fromIso(shown))}
										hideLabel
										size="sm">
										{arrows()}
									</InputGroup>
								)}
							</div>

							<div role="grid" aria-labelledby={headingId} className={css.days}>
								<div role="row" className={css.week}>
									{weekdays.map((day) => (
										<span
											key={day}
											role="columnheader"
											aria-label={weekdayLong.format(fromIso(day))}
											className={css.weekday}>
											{weekday.format(fromIso(day))}
										</span>
									))}
								</div>

								{weeksOf(shown, weekStartsOn).map((week) => (
									<div key={week[0]} role="row" className={css.week}>
										{week.map((day) => {
											const outside: boolean = monthOf(day) !== shown;
											const isEdge: boolean =
												!!range && (day === range.start || day === range.end);
											const isBetween: boolean =
												!!range?.end && day > range.start && day < range.end;

											return (
												<div
													key={day}
													role="gridcell"
													aria-selected={!outside && (isEdge || isBetween)}>
													<button
														type="button"
														data-date={day}
														data-selected={!outside && isEdge ? '' : undefined}
														data-in-range={
															!outside && isBetween ? '' : undefined
														}
														data-today={
															day === today && !outside ? '' : undefined
														}
														aria-current={
															day === today && !outside ? 'date' : undefined
														}
														aria-label={dayName.format(fromIso(day))}
														tabIndex={day === tabStop && !outside ? 0 : -1}
														disabled={outside}
														className={css.day}
														onClick={() => choose(day)}>
														{fromIso(day).getDate()}
													</button>
												</div>
											);
										})}
									</div>
								))}
							</div>
						</div>
					);
				})}
			</div>

			{bottomBar && <div className={css.bottomBar}>{bottomBar}</div>}
		</div>
	);
}
